import { branchNameMap } from "../../../../core/schema/Branch.js";
import type { Locale } from "../../../../core/schema/Locale.js";
import type { PickUpBranch } from "../../../../core/schema/PickUpBranch.js";
import type { ProductForCustomer } from "../../../../core/schema/Product.js";
import type { ProductAmountPerCategory } from "../../../../core/schema/ProductAmountPerCategory.js";
import type { ScheduledB2cOrderStopSettingItem } from "../../../../core/schema/ScheduledB2cOrderStopsSetting.js";
import { getNearestDeliveryDate } from "../../../../core/schema/utils/getNearestDeliveryDate.js";
import { areProductQuantitiesOverMax } from "../../../../core/utils/AreProductQuantitiesOverMax.js";
import { getProductsWithNotPermittedPickUpDateAtDate } from "../../../../core/utils/getProductsWithNotPermittedPickUpDateAtDate.js";
import { isDateDuringScheduledB2cOrderStop } from "../../../../core/utils/isDateDuringScheduledB2cOrderStop.js";
import { upperCaseFirstLetter } from "../../../../core/utils/upperCaseFirstLetter.js";
import type { Cart, CartInStep } from "../../stores/cart/Cart.js";
import { CartStep, cartStepOrderMap, cartStepPathMap } from "../../stores/cart/CartStep.js";
import type { CartStore } from "../../stores/cart/CartStore.js";
import { inflectProduct } from "./inflectProduct.js";

export function getCorrectCartInStepOrRedirect<Step extends CartStep>(
	cartStore: CartStore,
	locale: Locale,
	inStep: Step,
	cart: Cart,
	scheduledB2cOrderStops: ScheduledB2cOrderStopSettingItem[],
	stopB2cOrders: PickUpBranch[],
	maxProductsPerCategory: ProductAmountPerCategory,
): { cartInStep: CartInStep<Step>; redirected: boolean } {
	let redirected = false;
	if (typeof window === "undefined") {
		return {
			cartInStep: cart as CartInStep<Step>,
			redirected,
		};
	}

	const cartStepIndex = cartStepOrderMap[cart.step];
	const stepIndex = cartStepOrderMap[inStep];
	const { deliveryDate } = cart.order;

	// Clear outdated delivery date and force user to select a new one
	const paymentDeliveryIndex = cartStepOrderMap[CartStep.PaymentDelivery];
	if (cartStepIndex >= paymentDeliveryIndex && deliveryDate) {
		const date = new Date(deliveryDate);
		const nearestDeliveryDate = getNearestDeliveryDate(cart.productsInOrder);
		if (nearestDeliveryDate && date < nearestDeliveryDate) {
			// eslint-disable-next-line no-alert
			alert(
				`Nejbližší možné datum vyzvednutí je ${new Date(nearestDeliveryDate).toLocaleString()}, prosím zvolte jiný termín.`,
			);
			void cartStore.clearDeliveryDate();
		}
	}

	if (cartStepIndex > paymentDeliveryIndex && deliveryDate && cart.order.pickUpBranch !== undefined) {
		if (stopB2cOrders.includes(cart.order.pickUpBranch)) {
			// eslint-disable-next-line no-alert
			alert(`Z důvodu odstávky není možné odběr na pobočce ${branchNameMap[cart.order.pickUpBranch]}.`);
			void cartStore.clearPickUpBranch();
			window.location.replace(`/${cartStepPathMap[CartStep.PaymentDelivery][locale]}`);
			redirected = true;
		}

		const selectedDateIsDuringStop = isDateDuringScheduledB2cOrderStop(
			deliveryDate,
			cart.order.pickUpBranch,
			scheduledB2cOrderStops,
		);

		if (selectedDateIsDuringStop) {
			// eslint-disable-next-line no-alert
			alert(
				"Z důvodu odstávky není možné ve zvolený den na zvoleném místě vyzvednout objednávku, prosím zvolte jiné místo či termín.",
			);
			void cartStore.clearDeliveryDate();
			void cartStore.clearPickUpBranch();
			window.location.replace(`/${cartStepPathMap[CartStep.PaymentDelivery][locale]}`);
			redirected = true;
		}

		const productsWithNotPermittedPickUpDateAtDate = getProductsWithNotPermittedPickUpDateAtDate(
			deliveryDate,
			cart.productsInOrder,
		);

		if (productsWithNotPermittedPickUpDateAtDate.length > 0) {
			const formatter = new Intl.ListFormat("cs", { style: "long", type: "conjunction" });
			const productNames = productsWithNotPermittedPickUpDateAtDate.map(({ name }: ProductForCustomer) => name[locale]);
			const formattedProductNames = formatter.format(productNames);
			// eslint-disable-next-line no-alert
			alert(
				`${upperCaseFirstLetter(inflectProduct(productsWithNotPermittedPickUpDateAtDate.length))} ${formattedProductNames} nemají povolené vyzvednutí ve zvolený den, prosím zvolte jiné místo či termín.`,
			);
			void cartStore.clearDeliveryDate();
			window.location.replace(`/${cartStepPathMap[CartStep.PaymentDelivery][locale]}`);
			redirected = true;
		}
	}

	const overviewIndex = cartStepOrderMap[CartStep.Overview];
	if (stepIndex > overviewIndex) {
		for (const product of cart.order.products) {
			const productDetail = cart.productsInOrder.find(({ id }) => id === product.product.id);
			if (!productDetail) {
				throw new Error("Product not found in productsInOrder");
			}

			if (product.quantity < productDetail.minimumOrderQuantity) {
				window.location.replace(`/${cartStepPathMap[CartStep.Overview][locale]}`);
				redirected = true;
				break;
			}
		}

		if (areProductQuantitiesOverMax(cart.order.products, maxProductsPerCategory)) {
			window.location.replace(`/${cartStepPathMap[CartStep.Overview][locale]}`);
			redirected = true;
		}
	}

	if (stepIndex > paymentDeliveryIndex && !deliveryDate) {
		window.location.replace(`/${cartStepPathMap[CartStep.PaymentDelivery][locale]}`);
		redirected = true;
	}

	if (cartStepIndex < stepIndex) {
		window.location.replace(`/${cartStepPathMap[cart.step][locale]}`);
		redirected = true;
	}

	return {
		cartInStep: cart as CartInStep<Step>,
		redirected,
	};
}
